import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Home } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        ...credentials,
        remember_me: keepSignedIn
      });
      const { access_token, user } = response.data;

      localStorage.setItem('token', access_token);
      toast.success(`Welcome back, ${user.name}!`);

      if (user.role === 'admin' || user.role === 'super_admin') {
        navigate('/admin/dashboard');
      } else if (user.role === 'judge') {
        navigate('/judge/dashboard');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-gray-50">
      <div className="bg-white p-12 max-w-md w-full rounded-xl shadow-lg border border-gray-200">
        <div className="text-center mb-8">
          <img 
            src="/c4ac-logo.png" 
            alt="Cruise for a Cause" 
            className="h-32 mx-auto mb-6"
          />
          <h2 className="text-3xl font-bold text-gray-900">Judge/Admin Login</h2>
          <p className="text-gray-700 mt-2">Sign in to access your dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email" className="text-gray-700">Email</Label>
            <Input
              id="email"
              type="email"
              value={credentials.email}
              onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
              className="mt-2 bg-white border-gray-300 text-gray-900"
              placeholder="judge@example.com"
              required
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-gray-700">Password</Label>
            <Input
              id="password"
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              className="mt-2 bg-white border-gray-300 text-gray-900"
              placeholder="••••••••"
              required
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="keep-signed-in" 
              checked={keepSignedIn}
              onCheckedChange={setKeepSignedIn}
              className="border-gray-400 data-[state=checked]:bg-pink-600 data-[state=checked]:border-pink-600"
            />
            <label 
              htmlFor="keep-signed-in" 
              className="text-sm text-gray-700 cursor-pointer select-none"
            >
              Keep me signed in
            </label>
          </div>

          <Button
            type="submit"
            className="w-full btn-c4ac py-6 text-lg font-semibold"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/">
            <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
              <Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
